// Captures context about the host page the widget is embedded on, so the
// agent backend can ground answers in "the page the visitor is looking at"
// instead of relying purely on server-side crawled/indexed content.
//
// This must work on intranet/authenticated pages the backend can never
// reach on its own, so everything here is read from the browser that
// already rendered the page — nothing is fetched server-side.

export type PageContextPayload = {
  url: string
  title: string
  referrer?: string
  excerpt?: string
}

export type ClientMetaPayload = {
  userAgent?: string
  language?: string
  timezone?: string
  viewportWidth?: number
  viewportHeight?: number
}

const MAX_EXCERPT_LENGTH = 6000
const MAX_URL_LENGTH = 2048
const MAX_TITLE_LENGTH = 300
const MAX_META_SUMMARY_LENGTH = 500
// Below this length a matched <main>/<article> is treated as too thin to be
// the real content (e.g. a near-empty shell), and we fall back to <body>.
const MIN_CONTENT_ROOT_LENGTH = 200
const CONTENT_ROOT_SELECTOR = 'main, [role="main"], article'
const NON_PAGE_CONTENT_SELECTORS = [
  'script',
  'style',
  'noscript',
  'svg',
  'iframe',
  'template',
  '[aria-hidden="true"]',
]
// Structural chrome that's rarely the answer to "what's on this page" and
// otherwise eats into the excerpt's truncation budget ahead of real content.
const STRUCTURAL_CHROME_SELECTORS = [
  'nav',
  'header',
  'footer',
  'aside',
  '[role="navigation"]',
  '[role="banner"]',
  '[role="contentinfo"]',
]
const OSIRUS_WIDGET_SELECTORS = [
  '.osirus-agent',
  '.osirus-agent-overlay',
  '.osirus-agent-content',
  '.osirus-agent-flyout',
  '.osirus-agent-container',
  '[id="osirusai-agent"]',
  '[id^="osirusai-agent-"]',
  '[data-osirusai-css]',
  '[data-osirusai-bi]',
  '[data-osirus-asset-base]',
]
const REMOVAL_SELECTOR = [
  ...NON_PAGE_CONTENT_SELECTORS,
  ...STRUCTURAL_CHROME_SELECTORS,
  ...OSIRUS_WIDGET_SELECTORS,
].join(', ')

const truncate = (value: string, max: number): string =>
  value.length > max ? value.slice(0, max) : value

const cleanedText = (root: HTMLElement): string => {
  const clone = root.cloneNode(true) as HTMLElement
  clone.querySelectorAll(REMOVAL_SELECTOR).forEach((el) => el.remove())
  const text = clone.innerText || clone.textContent || ''
  return text.replace(/\s+/g, ' ').trim()
}

const extractVisibleText = (): string => {
  if (typeof document === 'undefined' || !document.body) return ''

  try {
    const contentRoot = document.querySelector<HTMLElement>(CONTENT_ROOT_SELECTOR)
    if (contentRoot) {
      const primaryText = cleanedText(contentRoot)
      if (primaryText.length >= MIN_CONTENT_ROOT_LENGTH) {
        return truncate(primaryText, MAX_EXCERPT_LENGTH)
      }
    }

    return truncate(cleanedText(document.body), MAX_EXCERPT_LENGTH)
  } catch {
    // DOM access can fail in unusual host environments (locked-down CSPs,
    // exotic embeds). Never let context capture break sending a message.
    return ''
  }
}

// Hand-authored summaries (SEO meta tags, structured data) are usually
// higher-signal than anything pulled out of body text, and cost nothing to
// read, so they're surfaced ahead of the extracted excerpt.
const extractMetaSummary = (): string => {
  if (typeof document === 'undefined') return ''

  try {
    const metaDescription =
      document.querySelector('meta[name="description"]')?.getAttribute('content')?.trim() ||
      document.querySelector('meta[property="og:description"]')?.getAttribute('content')?.trim() ||
      ''
    if (metaDescription) return truncate(metaDescription, MAX_META_SUMMARY_LENGTH)

    const ldJsonScripts = Array.from(document.querySelectorAll('script[type="application/ld+json"]'))
    for (const script of ldJsonScripts) {
      try {
        const parsed = JSON.parse(script.textContent || '')
        const nodes = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.['@graph'])
            ? parsed['@graph']
            : [parsed]
        for (const node of nodes) {
          const description = typeof node?.description === 'string' ? node.description.trim() : ''
          if (description) return truncate(description, MAX_META_SUMMARY_LENGTH)
        }
      } catch {
        continue
      }
    }

    return ''
  } catch {
    return ''
  }
}

export const capturePageContext = (): PageContextPayload | null => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null

  try {
    const metaSummary = extractMetaSummary()
    const visibleText = extractVisibleText()
    const excerpt = metaSummary
      ? truncate(`${metaSummary}\n\n${visibleText}`.trim(), MAX_EXCERPT_LENGTH)
      : visibleText

    return {
      url: truncate(window.location.href || '', MAX_URL_LENGTH),
      title: truncate(document.title || '', MAX_TITLE_LENGTH),
      referrer: document.referrer ? truncate(document.referrer, MAX_URL_LENGTH) : undefined,
      excerpt: excerpt || undefined,
    }
  } catch {
    return null
  }
}

export const captureClientMeta = (): ClientMetaPayload | null => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return null

  try {
    return {
      userAgent: navigator.userAgent || undefined,
      language: navigator.language || undefined,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
      viewportWidth: window.innerWidth || undefined,
      viewportHeight: window.innerHeight || undefined,
    }
  } catch {
    return null
  }
}
